import { matchPath, useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import {
  getAllArtifacts,
  type ArtifactDefinition,
  type ArtifactId,
} from '@/artifacts';
import { useGeoMarkupStore } from '@/store';

/**
 * Workspace-scoped base path for all artifact detail routes. Kept in sync
 * with `useSyncOverlayWithUrl` — both files rely on the same segment
 * order so the URL parser and the active-artifact resolver agree.
 */
const MAP_BASE_PATH = '/w/:shortId/map';

/**
 * Resolve which artifact's detail panel should be active right now.
 *
 * Resolution priority:
 *   1. URL match — if the pathname matches `/w/:shortId/map/<singular>/:itemId`
 *      (detail route), that artifact wins regardless of store flags.
 *   2. Store flag — if no URL match but exactly one artifact has
 *      `detailsIsOpen`, use that (covers transitional states and
 *      programmatic opens without URL changes).
 *
 * The hook returns `isAnyDetailOpen` separately so the shell can drive
 * the SlidePanel `opened` prop with a single OR across artifact slices.
 */
export function useActiveDetailArtifact(): {
  activeArtifactId: ArtifactId | null;
  isAnyDetailOpen: boolean;
} {
  const location = useLocation();

  // Registry-driven: subscribe to every artifact's detail flag so a newly
  // registered artifact (e.g. obstructions) participates without editing this
  // hook. Hardcoding tasks/notes here previously left obstruction details
  // unable to open the shared SlidePanel.
  const detailOpenById = useGeoMarkupStore(
    useShallow((state) => {
      const flags = {} as Record<ArtifactId, boolean>;
      for (const artifact of getAllArtifacts()) {
        flags[artifact.id as ArtifactId] =
          artifact.getSlice(state).detailsIsOpen;
      }
      return flags;
    })
  );

  const isAnyDetailOpen = Object.values(detailOpenById).some(Boolean);

  const artifactFromUrl = findArtifactByDetailUrl(location.pathname);
  if (artifactFromUrl) {
    return {
      activeArtifactId: artifactFromUrl.id as ArtifactId,
      isAnyDetailOpen,
    };
  }

  // URL didn't match any detail route — fall back to whichever slice flag
  // is currently set. When none is set (panel closed), return null so the
  // shell renders no content while the SlidePanel plays its exit animation.
  for (const artifact of getAllArtifacts()) {
    const artifactId = artifact.id as ArtifactId;
    if (detailOpenById[artifactId]) {
      return { activeArtifactId: artifactId, isAnyDetailOpen };
    }
  }
  return { activeArtifactId: null, isAnyDetailOpen };
}

function findArtifactByDetailUrl(pathname: string): ArtifactDefinition | null {
  for (const artifact of getAllArtifacts()) {
    const match = matchPath(
      { path: `${MAP_BASE_PATH}/${artifact.singular}/:itemId`, end: true },
      pathname
    );
    if (match?.params.itemId && match.params.itemId !== 'new') {
      return artifact;
    }
  }
  return null;
}
